import React, { useState, useRef } from 'react'
import { usePostHog } from 'posthog-js/react'

const imgFrame2147229011 = '/icon/cta-bg.jpg'

const CTA = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const emailInputRef = useRef(null)
  const posthog = usePostHog()

  // Generate a strong random password
  const generateStrongPassword = () => {
    const length = 32
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=[]{}|;:,.<>?'
    const values = crypto.getRandomValues(new Uint32Array(length))
    let password = ''
    for (let i = 0; i < length; i++) {
      password += charset[values[i] % charset.length]
    }
    return password
  }

  // Create user account
  const createUserAccount = async (email) => {
    try {
      const password = generateStrongPassword()

      const response = await fetch('https://api.insightssystem.com/api:-4GSCDHb/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: '',
          email: email,
          password: password,
          phone_number: 0,
          reason_of_automate: 'I came from CTA section for automation',
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))

        if (
          response.status === 403 &&
          (errorData.code === 'ERROR_CODE_ACCESS_DENIED' || errorData.message?.includes('already in use'))
        ) {
          return { exists: true }
        }

        console.error('Failed to create user account:', errorData)
        throw new Error('Failed to create user account')
      }

      const data = await response.json()
      const authToken = data.authToken || data.at || data.token

      if (!authToken) {
        throw new Error('No auth token in response')
      }

      return { at: authToken, password }
    } catch (error) {
      console.error('Error creating user account:', error)
      return null
    }
  }

  // Login with email (for existing users)
  const loginWithEmail = async (email) => {
    try {
      const response = await fetch('https://api.insightssystem.com/api:-4GSCDHb/auth/login_with_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('Failed to login with email:', errorData)
        throw new Error('Failed to login with email')
      }

      const data = await response.json()
      const authToken = data.authToken || data.at || data.token

      if (!authToken) {
        throw new Error('No auth token in response')
      }

      return { at: authToken }
    } catch (error) {
      console.error('Error logging in with email:', error)
      return null
    }
  }

  // Send user credentials via email (non-critical)
  const sendUserCredentials = async (email, password) => {
    try {
      const response = await fetch('https://api.insightssystem.com/api:-4GSCDHb/send_mail_user_credential', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password_text: password,
        }),
      })

      if (!response.ok) {
        console.warn('Failed to send credentials email (non-critical):', await response.text().catch(() => ''))
      }
    } catch (error) {
      console.warn('Error sending credentials email (non-critical):', error)
    }
  }

  // Collect email - similar to ComparisonTable.astro
  const collectEmail = async (email, source) => {
    try {
      // Create user account first
      let userResult = await createUserAccount(email)

      if (!userResult) {
        return { success: false, message: 'Failed to create account. Please try again.' }
      }

      // Check if user already exists
      if ('exists' in userResult && userResult.exists) {
        // User exists, login instead
        const loginResult = await loginWithEmail(email)
        if (!loginResult) {
          return { success: false, message: 'Failed to login. Please try again.' }
        }

        // Send credentials email with "exist" as password (non-critical)
        await sendUserCredentials(email, 'exist')
      } else if ('at' in userResult && 'password' in userResult) {
        // New user created
        // Send credentials email (non-critical)
        await sendUserCredentials(email, userResult.password)
      }

      return { success: true }
    } catch (error) {
      console.error('Error collecting email:', error)
      return { success: false, message: 'Something went wrong. Please try again.' }
    }
  }

  // Validate email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    const emailValue = email.trim()

    if (!emailValue) {
      setError('Please enter your email address')
      return
    }

    if (!validateEmail(emailValue)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)

    try {
      const result = await collectEmail(emailValue, 'cta_section')

      if (result.success) {
        // Store email in localStorage
        localStorage.setItem('notifyer_user_email', emailValue)
        posthog?.identify(emailValue, { email: emailValue })
        posthog?.capture('free_trial_started', { email: emailValue, source: 'cta_section' })

        // Show success state
        setShowSuccess(true)

        // Redirect after delay (similar to ComparisonTable.astro)
        setTimeout(() => {
          const baseUrl =
            window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1' ||
              window.location.hostname.includes('localhost')
              ? 'http://localhost:3000/pricing'
              : 'https://api.insightssystem.com/api:ox_LN9zX/yt_ad'

          const params = new URLSearchParams()
          params.append('email', emailValue)

          // Add all cookies to params
          const cookies = document.cookie.split(';')
          cookies.forEach((cookie) => {
            const [name, value] = cookie.trim().split('=')
            if (name && value) {
              params.append(name, decodeURIComponent(value))
            }
          })

          const finalUrl = `${baseUrl}?${params.toString()}`
          window.location.href = finalUrl
        }, 1500)
      } else {
        setError(result.message || 'Something went wrong. Please try again.')
        setIsLoading(false)
      }
    } catch (err) {
      console.error('CTA: Email collection error:', err)
      posthog?.captureException(err)
      setError('Something went wrong. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <section className="bg-white px-4 py-10 sm:px-8 sm:py-12 lg:px-[100px] lg:py-16">
      <div className="mx-auto max-w-[1240px] rounded-[18px] border border-[#ececec] bg-[#fafaf9] p-2">
        <div
          className="relative overflow-hidden rounded-[14px] px-6 py-12 sm:px-10 lg:px-16 lg:py-16"
          style={{
            backgroundColor: '#0b0f14',
            backgroundImage: `url(${imgFrame2147229011})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b0f14]/70 via-[#0b0f14]/60 to-[#0b0f14]/40" aria-hidden="true" />

          <div className="relative flex flex-col items-center gap-6 text-center text-white">
            <h2 className="text-[28px] font-semibold leading-tight tracking-[-1px] sm:text-[36px] lg:text-[44px]">
              <span className="text-[#01cd74]">Ready to transform</span> your
              <br className="hidden sm:block" />
              WhatsApp communication?
            </h2>
            <p className="max-w-3xl text-[15px] leading-[1.7] tracking-[-0.4px] text-[#e7f0ec] sm:text-[16px]">
              Join thousands of businesses already using Notifyer to automate their WhatsApp messaging and improve
              customer engagement.
            </p>

            {!showSuccess ? (
              <form onSubmit={handleSubmit} className="w-full max-w-md">
                <div className="flex flex-col items-center gap-3 sm:flex-row sm:bg-white/10 sm:px-3 sm:py-2 sm:rounded-full sm:border sm:border-white/20 sm:shadow-[0px_4px_12px_rgba(0,0,0,0.25)]">
                  <div className="w-full sm:w-auto sm:flex-1">
                    <input
                      ref={emailInputRef}
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        setError('')
                      }}
                      placeholder="Enter your email address"
                      className="w-full rounded-full border border-transparent bg-white/10 px-5 py-3 text-sm text-white placeholder:text-white/70 focus:border-white/50 focus:outline-none sm:bg-transparent sm:px-4"
                      disabled={isLoading}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full rounded-full bg-[#01cd74] px-6 py-3 text-sm font-semibold tracking-[-0.2px] text-[#0f1b14] shadow-[0px_4px_10px_rgba(1,205,116,0.35)] transition duration-150 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Creating account...
                      </span>
                    ) : (
                      'Start Free Trial'
                    )}
                  </button>
                </div>
                {error && (
                  <div className="mt-3 text-sm text-red-300 flex items-center justify-center gap-1.5">
                    <svg
                      className="h-4 w-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
              </form>
            ) : (
              <div className="text-center space-y-3">
                <div className="flex justify-center">
                  <div className="relative">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#01cd74] to-[#01cd74]/80 shadow-lg animate-pulse">
                      <svg
                        className="h-7 w-7 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div className="absolute inset-0 rounded-full bg-[#01cd74]/20 animate-ping" />
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-[#e7f0ec]">
                    Confirmation sent to <span className="font-semibold text-[#01cd74]">{email}</span>
                  </p>
                  <p className="text-xs text-[#e7f0ec]/70">Redirecting to your account...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default CTA

